/**
 * Template Section Component
 *
 * Horizontal scrolling section with title and "See all" link.
 * Wraps TemplateCard components in a scrollable row.
 */

import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface TemplateSectionProps {
  title: string;
  onSeeAll?: () => void;
  children: React.ReactNode;
  themeStyles: {
    text: string;
    textDim: string;
    border: string;
  };
}

// ============================================================================
// Component
// ============================================================================

export function TemplateSection({
  title,
  onSeeAll,
  children,
  themeStyles,
}: TemplateSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 400;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-base font-semibold"
          style={{ color: themeStyles.text }}
        >
          {title}
        </h3>
        <div className="flex items-center gap-2">
          {/* Scroll buttons */}
          <button
            onClick={() => scroll('left')}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{
              background: 'rgba(255,255,255,0.05)',
              color: themeStyles.textDim,
            }}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{
              background: 'rgba(255,255,255,0.05)',
              color: themeStyles.textDim,
            }}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {/* See all link */}
          {onSeeAll && (
            <button
              onClick={onSeeAll}
              className="text-sm font-medium transition-colors hover:opacity-80 ml-2"
              style={{ color: themeStyles.textDim }}
            >
              See all
            </button>
          )}
        </div>
      </div>

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
